// Inline confirmation card for a staged action batch (docs/05 rule #2). Renders
// the human-readable action list, Apply/Cancel, and — once applied — per-action
// results linking to the created entities. Expired batches render disabled.

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Button, Badge } from '../components/ui.tsx';
import { CheckMark, CrossMark } from '../components/StatusMarks.tsx';
import { scheduleWindowSummary } from '../lib/schedule-summary.ts';
import { mediaFileUrl } from '../api/media.ts';
import { SandboxedHtmlFrame } from '../components/SandboxedHtmlFrame.tsx';
import type { BatchState, BatchAction, ActionResult, BatchStatus } from './types.ts';

/** The blast-radius plan for a staged action (docs/05 §Change plan): a compact from→to diff,
 *  the affected-screens line with a live-now badge, and the timing footnote. Built to READ in
 *  ~3 seconds — one small table, one line, one footnote. */
function PlanBlock({ plan }: { plan: NonNullable<BatchAction['plan']> }) {
  const scr = plan.affected_screens;
  return (
    <div className="asst-plan">
      {plan.diff.length > 0 && (
        <table className="asst-plan-diff">
          <tbody>
            {plan.diff.map((d, i) => (
              <tr key={i}>
                <th scope="row">{d.field}</th>
                <td className="asst-plan-from">{d.from}</td>
                <td className="asst-plan-arrow" aria-hidden>
                  →
                </td>
                <td className="asst-plan-to">{d.to}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="asst-plan-affected">
        {scr.count > 0 ? (
          <>
            <span>
              Affects {scr.count} screen{scr.count === 1 ? '' : 's'}
              {scr.names.length > 0 && <span className="text-dim"> · {scr.names.join(', ')}</span>}
            </span>
            <Badge tone={scr.live_now ? 'warn' : 'info'}>
              {scr.live_now ? 'Playing now' : 'Future windows'}
            </Badge>
          </>
        ) : (
          <span className="text-dim">No screens affected yet</span>
        )}
      </div>
      <div className="asst-plan-timing text-dim">{plan.timing}</div>
    </div>
  );
}

/** Destructive-batch controls (docs/05 §Change plan / destructive tier): a deliberate
 *  acknowledgement gate that must be ticked before the red confirm unlocks — a delete can't
 *  be undone, so it never fires on a single stray click. */
function DestructiveConfirm({
  batchId,
  onConfirm,
}: {
  batchId: string;
  onConfirm: (batchId: string, approve: boolean) => void;
}) {
  const [armed, setArmed] = useState(false);
  return (
    <div className="asst-destructive-confirm">
      <label className="asst-destructive-ack">
        <input type="checkbox" checked={armed} onChange={(e) => setArmed(e.target.checked)} />
        I understand this can’t be undone
      </label>
      <div className="asst-card-actions">
        <Button
          variant="danger"
          size="sm"
          disabled={!armed}
          onClick={() => onConfirm(batchId, true)}
        >
          Delete
        </Button>
        <Button size="sm" onClick={() => onConfirm(batchId, false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/** Visual preview of the media a staged action will create (docs/09 §B2). Images
 *  render as <img>; HTML kinds (and unknown kinds, safely) render in a sandboxed
 *  16:9 iframe. A badge marks the media as a not-yet-live draft, or discarded once
 *  the batch is rejected. */
function ActionPreviewBlock({
  preview,
  status,
}: {
  preview: NonNullable<BatchAction['preview']>;
  status: BatchStatus;
}) {
  const discarded = status === 'rejected';
  const isImage = preview.media_kind === 'image';
  return (
    <div className={`asst-preview${discarded ? ' discarded' : ''}`}>
      <div className="asst-preview-stage">
        {isImage ? (
          <img src={mediaFileUrl(preview.media_id)} alt="Draft preview" />
        ) : (
          <SandboxedHtmlFrame mediaId={preview.media_id} title="Draft preview" />
        )}
      </div>
      <Badge tone={discarded ? 'neutral' : 'info'}>
        {discarded ? 'Draft discarded' : 'Draft — not live yet'}
      </Badge>
    </div>
  );
}

/** Loose text equality (trim, collapse inner whitespace, case-insensitive) — used to detect
 *  a bullet that merely restates the card title for a single-action batch. */
function sameText(a: string, b: string): boolean {
  const norm = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();
  return norm(a) === norm(b);
}

/** Enrich a schedule action's line with the plain-words window (reusing the one
 *  renderer shared with the Schedules table) when the args carry a window. */
function actionDetail(action: BatchAction): string | null {
  if (action.kind !== 'create_schedule' && action.kind !== 'update_schedule') return null;
  const a = action.args as Record<string, unknown>;
  const hasWindow =
    'days_mask' in a || 'start_time' in a || 'end_time' in a || 'start_date' in a || 'end_date' in a;
  if (!hasWindow) return null;
  return scheduleWindowSummary({
    days_mask: typeof a.days_mask === 'number' ? a.days_mask : 127,
    start_time: (a.start_time as string) ?? null,
    end_time: (a.end_time as string) ?? null,
    start_date: (a.start_date as string) ?? null,
    end_date: (a.end_date as string) ?? null,
  });
}

/** Deep link to a created entity, or null when there's nowhere to point. */
function ResultLink({ result }: { result: ActionResult }) {
  const { entity_type, entity_id } = result;
  if (!entity_id) return null;
  switch (entity_type) {
    case 'playlist':
      return (
        <Link to="/playlists/$playlistId" params={{ playlistId: entity_id }}>
          View playlist
        </Link>
      );
    case 'schedule':
      return (
        <Link to="/schedules" search={{ edit: entity_id }}>
          View schedule
        </Link>
      );
    case 'screen':
      return (
        <Link to="/screens" search={{ screen: entity_id }}>
          View screen
        </Link>
      );
    case 'group':
      return (
        <Link to="/screens" search={{ groups: true }}>
          View groups
        </Link>
      );
    default:
      return null;
  }
}

export function ConfirmationCard({
  batch,
  onConfirm,
}: {
  batch: BatchState;
  onConfirm: (batchId: string, approve: boolean) => void;
}) {
  const { status } = batch;
  const actionable = status === 'pending';
  const busy = status === 'applying';
  const destructive = batch.actions.some((a) => a.destructive);

  return (
    <div
      className={`asst-card asst-card-${status}${destructive ? ' asst-card-destructive' : ''}`}
      role="group"
      aria-label={batch.title}
    >
      <div className="asst-card-head">
        <strong>{batch.title}</strong>
        {destructive && <Badge tone="warn">Can’t be undone</Badge>}
        {status === 'applied' && <Badge tone="ok">Applied</Badge>}
        {status === 'failed' && <Badge tone="warn">Partly failed</Badge>}
        {status === 'rejected' && <Badge tone="neutral">Dismissed</Badge>}
        {status === 'expired' && <Badge tone="neutral">Expired</Badge>}
      </div>

      <ul className="asst-actions">
        {batch.actions.map((action, i) => {
          const result = batch.results?.find((r) => r.action_index === i);
          const detail = actionDetail(action);
          // For a single-action batch the server uses the action summary AS the card title, so
          // repeating it in the bullet is pure duplication — suppress it (the plan/detail still
          // render). Multi-action batches always show every summary.
          const dupTitle = batch.actions.length === 1 && sameText(action.summary, batch.title);
          return (
            <li key={i} className="asst-action">
              <span className="asst-action-dot" aria-hidden>
                {result ? result.ok ? <CheckMark /> : <CrossMark /> : '•'}
              </span>
              <span className="asst-action-body">
                {!dupTitle && <span>{action.summary}</span>}
                {detail && (
                  <span className="text-dim">{dupTitle ? detail : ` — ${detail}`}</span>
                )}
                {action.plan && <PlanBlock plan={action.plan} />}
                {action.preview && <ActionPreviewBlock preview={action.preview} status={status} />}
                {result?.ok && (
                  <span className="asst-action-link">
                    <ResultLink result={result} />
                  </span>
                )}
                {result && !result.ok && result.error && (
                  <span className="error-text"> {result.error}</span>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      {actionable &&
        (destructive ? (
          <DestructiveConfirm batchId={batch.batch_id} onConfirm={onConfirm} />
        ) : (
          <div className="asst-card-actions">
            <Button variant="primary" size="sm" onClick={() => onConfirm(batch.batch_id, true)}>
              Apply
            </Button>
            <Button size="sm" onClick={() => onConfirm(batch.batch_id, false)}>
              Cancel
            </Button>
          </div>
        ))}
      {busy && <div className="text-dim asst-card-note">Applying…</div>}
      {status === 'expired' && (
        <div className="text-dim asst-card-note">Expired — ask again to redo this.</div>
      )}
      {status === 'failed' && batch.error && (
        <div className="error-text asst-card-note">{batch.error}</div>
      )}
    </div>
  );
}
